import { useState } from 'react';
import { uniqueId } from 'lodash';

import {
  DepositAddIntegrationLayout,
  CustomAssetFormTable,
  type ICustomAssetTableRow,
} from 'src/components/Deposit/Add';
import { CustomIntegrationProviderEnum } from 'src/graphql/schema';
import { useAddDeposit } from 'src/context/deposit';

type Props = {
  customValidator?: (
    asset: {
      title?: string;
      url: string;
    },
    currentItems: ICustomAssetTableRow[],
  ) => Promise<void>;
};

const CustomDepositContainer = ({ customValidator }: Props) => {
  const {
    ui: { integration, customData, setCustomData, getBack, history },
  } = useAddDeposit();

  const [dataSource, setDataSource] = useState<ICustomAssetTableRow[]>(
    customData.map((item) => ({
      id: uniqueId(),
      title: item.title || '',
      url: item.url,
      isEditable: false,
    })),
  );

  const provider = integration?.platform as CustomIntegrationProviderEnum;

  const onAdd = ({ title, url }: { title?: string; url: string }) =>
    setDataSource((prev) => [...prev, { title: title || '', url, id: uniqueId() }]);

  const onRemove = (key: string) => setDataSource((prev) => prev.filter((item) => item.id !== key));

  const onConfirm = () => {
    const assetsInput = dataSource.map(({ title, url }) => ({
      url,
      ...(title && { title }),
    }));
    setCustomData(assetsInput);
  };

  return (
    <DepositAddIntegrationLayout
      integration={{
        provider: integration?.platform || '',
        account: integration?.name || '',
      }}
      onConfirm={onConfirm}
      confirmButton={{
        disabled: !dataSource.length,
      }}
      onBack={getBack}
      isBackButtonVisible={history.length > 1}
    >
      <div className="px-6">
        <div className="pb-6 space-y-1.5">
          <h4 className="text-lg font-bold text-foreground">Asset Details</h4>
          <p className="text-sm text-muted-foreground">Add the details of the asset you want to deposit into escrow.</p>
        </div>
        <CustomAssetFormTable
          provider={provider}
          dataSource={dataSource}
          onAdd={onAdd}
          onRemove={onRemove}
          customValidator={customValidator}
        />
      </div>
    </DepositAddIntegrationLayout>
  );
};

export default CustomDepositContainer;
